import { useOutletContext } from "react-router-dom";
import * as Team from "src/state/modules/team";
import * as Event from "src/state/modules/event";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import { UseGameStateResult } from "src/components/util/useGameState";
import { BuiltGameState, SaberAtBat, SaberGameSnapshot } from "src/utils/sabertooth-client";
import {
  PlayerMap,
  atBatResultText,
  atBatTime,
  ordinalInning,
  outsRecordedIn,
  pitchSequenceText,
  playerFullName,
  teamAbbrev,
} from "./game-stream-helpers";
import Styles from "./PlaysPage.module.scss";

type Ctx = { team: Team.Team; event: Event.Event; gameState: UseGameStateResult };

interface HalfInning {
  inning: number;
  half: "top" | "bottom";
  atBats: SaberAtBat[];
}

export default function PlaysPage() {
  const { team, event, gameState } = useOutletContext<Ctx>();
  const { built, state, players, isLoading, isError } = gameState;

  if (isLoading && !state) return <PageLoader1 />;
  if (isError || !state || !built) {
    return <div className={Styles.empty}>Plays aren’t available for this game yet.</div>;
  }

  const oppName = event.opponentTeam?.name ?? "Opponent";
  const teamName = (id: string) => (id === team.id ? team.name : oppName);

  return <PlaysView built={built} state={state} players={players} teamName={teamName} />;
}

interface PlaysViewProps {
  built: BuiltGameState;
  state: SaberGameSnapshot;
  players: PlayerMap;
  // Maps a stream team id to its display name (see BoxScoreView). Drives the half-inning headers.
  teamName: (id: string) => string;
}

// The presentational play-by-play: completed at-bats grouped into half-innings (newest first) plus
// the current batter. Reused by the event Plays tab and the opponent modal.
export function PlaysView({ built, state, players, teamName }: PlaysViewProps) {
  // The batting team is away in the top half, home in the bottom half.
  const battingTeamName = (half: "top" | "bottom") =>
    teamName(half === "top" ? built.awayTeamId : built.homeTeamId);

  const awayAbbr = teamAbbrev(teamName(built.awayTeamId));
  const homeAbbr = teamAbbrev(teamName(built.homeTeamId));

  // Group completed at-bats into half-innings (chronological), then show newest first.
  // Group by `initialSituation` (the half-inning the at-bat happened in), NOT `result.situation`:
  // the engine rolls `result.situation` forward to the next half on the play that retires the side,
  // so grouping by it files every inning-ending out under the next half — i.e. the other team.
  const groups: HalfInning[] = [];
  for (const ab of state.fullPlayByPlay.completedAtBats) {
    const { inning, half } = ab.initialSituation;
    const last = groups[groups.length - 1];
    if (last && last.inning === inning && last.half === half) last.atBats.push(ab);
    else groups.push({ inning, half, atBats: [ab] });
  }
  groups.reverse();
  groups.forEach((g) => g.atBats.reverse());

  // No one is "at bat" once the game is final (the engine still reports a leadoff batter for the
  // half it rolled into after the last out).
  const current = built.gameOver ? null : state.fullPlayByPlay.currentBatter;

  // The current batter shares a half-inning with the newest completed group whenever the side
  // hasn't been retired yet (< 3 outs). In that case the "at bat" row belongs under that group's
  // existing header — rendering its own headered block would duplicate the header. Only when the
  // batter has rolled into a fresh half-inning (3 outs) does it need a standalone header.
  const newest = groups[0];
  const currentInNewestGroup =
    current != null && newest != null && newest.inning === state.inning && newest.half === state.half;

  const currentAtBatRow = current && (
    <div className={Styles.atBatRow}>
      <div className={Styles.headline}>{playerFullName(players[current])} at bat</div>
      <div className={Styles.meta} />
      <div className={Styles.description} />
    </div>
  );

  return (
    <div className={Styles.plays}>
      {current && !currentInNewestGroup && (
        <div className={Styles.group}>
          <div className={Styles.groupHeader}>
            {state.half === "top" ? "Top" : "Bottom"} {ordinalInning(state.inning)} - {battingTeamName(state.half)}
          </div>
          {currentAtBatRow}
        </div>
      )}

      {groups.map((g, gIdx) => (
        <div className={Styles.group} key={`${g.inning}-${g.half}`}>
          <div className={Styles.groupHeader}>
            {g.half === "top" ? "Top" : "Bottom"} {ordinalInning(g.inning)} - {battingTeamName(g.half)}
          </div>
          {currentInNewestGroup && gIdx === 0 && currentAtBatRow}
          {g.atBats.map((ab, idx) => (
            <AtBatRow key={idx} atBat={ab} players={players} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
          ))}
        </div>
      ))}
    </div>
  );
}

interface AtBatRowProps {
  atBat: SaberAtBat;
  players: PlayerMap;
  awayAbbr: string;
  homeAbbr: string;
}

function AtBatRow({ atBat, players, awayAbbr, homeAbbr }: AtBatRowProps) {
  const { situation } = atBat.result;
  // `situation.outs` resets to 0 on the play that retires the side, so derive the out count from the
  // outs recorded on this play tallied onto the at-bat's starting count — matches how the engine
  // numbers outs (see renderForScorebook in sabertooth.js). Includes between-pitch outs (a runner
  // caught stealing / picked off), which live on child events, not the final-play participants.
  const outsRecorded = outsRecordedIn(atBat);
  const outsAfter = (atBat.initialSituation?.outs ?? 0) + outsRecorded;
  const madeOut = outsRecorded > 0;
  const scoreChanged =
    situation.homeScore !== atBat.initialSituation?.homeScore ||
    situation.awayScore !== atBat.initialSituation?.awayScore;

  const pitchText = pitchSequenceText(atBat, players);
  const resultText = atBatResultText(atBat, players);
  const timeText = atBatTime(atBat);

  return (
    <div className={Styles.atBatRow}>
      <div className={Styles.headline}>
        {atBat.headline}
        {timeText && <span className={Styles.time}>{timeText}</span>}
      </div>
      <div className={Styles.meta}>
        {(scoreChanged || madeOut) && (
          <span className={Styles.score}>
            {awayAbbr} {situation.awayScore} - {homeAbbr} {situation.homeScore}
          </span>
        )}
        {madeOut && (
          <span className={Styles.outs}>
            {outsAfter} Out{outsAfter === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className={Styles.description}>
        {pitchText && <div className={Styles.pitchLine}>{pitchText}</div>}
        {resultText && <div className={Styles.resultLine}>{resultText}</div>}
      </div>
    </div>
  );
}
